import axios, { AxiosResponse } from 'axios';

export interface Paginated<T> {
  value: T[];
  nextLink?: string;
  totalCount?: number;
}

/**
 * The frontend uses Paginated<T> type where "value" cannot be null,
 * but the backend type also allows for null.
 * This type represents the backend type.
 *
 * @todo Consolidate Paginated<T> and PaginatedWithNullableNextLink<T>
 */
export interface PaginatedWithNullableNextLink<T> {
  value: T[];
  nextLink?: string | null;
  totalCount?: number;
}

/**
 * Loads all subsequent pages of a paginated API endpoint.
 *
 * @param authToken MNSPro bearer token
 * @param initialResponse The response object of the first
 *  request of the paginated api call. This has to be made
 *  outside this function.
 */
export const paginationLoadRemainingPages = async <
  InnerType,
  PaginatedType extends PaginatedWithNullableNextLink<InnerType>,
>(
  authToken: string,
  initialResponse: AxiosResponse<PaginatedType>,
) => {
  let nextLink: string | null | undefined = initialResponse.data.nextLink;
  let records: InnerType[] = initialResponse.data.value;

  while (nextLink) {
    const pageResponse: AxiosResponse<PaginatedType> =
      await axios.get<PaginatedType>(nextLink, {
        headers: {
          authorization: 'Bearer ' + authToken,
        },
      });
    records = [...records, ...pageResponse.data.value];
    nextLink = pageResponse.data.nextLink;
  }

  return records;
};
